import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  PDKMessageIcon,
  PDKPauseIcon,
  PDKResumeIcon,
} from '@/components/ui/pdk-icons';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Toggle } from '@/components/ui/toggle';
import { useDealContext } from '@/contexts/deal-provider';
import useAgentConvoPauseMutation from '@/hooks/use-agent-convo-pause-mutation';
import { useDealByShortId } from '@/hooks/use-deals-selectors';
import {
  FEATURE_FLAG_NAMES,
  useIsFeatureEnabled,
} from '@/hooks/use-feature-flags';
import useKickoffContinueFlowMutation from '@/hooks/use-kickoff-continue-flow-mutation';
import useSendSMSMutation from '@/hooks/use-send-sms-mutation';
import useSmsFlowStepMutation, {
  type Flow,
  type FlowStep,
} from '@/hooks/use-sms-flow-step-mutation';
import { toast } from '@/hooks/use-toast';
import { SMSAgentMessage } from '@/models/registry';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import { Send } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useRef, useState } from 'react';

const FLOW_OPTIONS: { value: Flow; label: string }[] = [
  {
    value: 'confirm_primary_customer_info',
    label: 'Confirm Customer Info',
  },
  {
    value: 'confirm_primary_customer_delivery_address',
    label: 'Confirm Customer Delivery Address',
  },
  {
    value: 'confirm_co_registrant_info',
    label: 'Confirm Co-Registrant Info',
  },
] as const;

const FLOW_STEP_OPTIONS = [
  { value: 'init', label: 'Init' },
  { value: 'sending_initial', label: 'Sending Initial' },
  { value: 'awaiting_reply', label: 'Awaiting Reply' },
  { value: 'processing_reply', label: 'Processing Reply' },
  { value: 'completed', label: 'Completed' },
  { value: 'error', label: 'Error' },
] as const;

const AgentConvo = observer(
  ({ smsConversation }: { smsConversation: SMSAgentMessage[] | null }) => {
    const { isFeatureEnabled } = useIsFeatureEnabled({
      featureFlagName: FEATURE_FLAG_NAMES.AGENT_CONVO,
    });

    const { isFeatureEnabled: isAgentConvoReplyEnabled } = useIsFeatureEnabled({
      featureFlagName: FEATURE_FLAG_NAMES.AGENT_CONVO_REPLY,
    });

    const { currentShortId } = useDealContext();
    const deal = useDealByShortId(currentShortId);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [messageText, setMessageText] = useState('');

    const { agentConvoPauseMutation } = useAgentConvoPauseMutation({
      dealId: deal?.id,
      shortId: deal?.short_id,
    });

    const { sendSMSMutation } = useSendSMSMutation({});

    const { smsFlowStepMutation } = useSmsFlowStepMutation({
      dealId: deal?.id,
      shortId: deal?.short_id,
      onSuccess: ({ sms_flow, sms_flow_step }) => {
        const updateType = sms_flow ? 'flow' : 'step';
        toast({
          title: `SMS ${updateType} updated`,
          description: sms_flow || sms_flow_step || 'Updated successfully',
        });
      },
      onError: (error) => {
        toast({
          title: 'Error updating SMS flow',
          description: error.message,
        });
      },
    });

    const { kickoffConfirmFlowMutation } = useKickoffContinueFlowMutation({
      dealId: deal?.id,
      shortId: deal?.short_id,
      onSuccess: () => {
        toast({
          title: 'Kickoff/Continue flow started',
          description:
            'The customer confirmation/continue flow has been initiated.',
        });
      },
      onError: (error) => {
        toast({
          title: 'Error starting kickoff/continue flow',
          description: error.message,
        });
      },
    });

    const isAgentPaused = deal?.agent_convo_paused ?? false;

    const handleTogglePause = () => {
      agentConvoPauseMutation.mutate({
        agentConvoPaused: !isAgentPaused,
      });
    };

    const handleSendMessage = () => {
      if (!messageText.trim() || !deal?.id) return;

      sendSMSMutation.mutate({
        text: messageText,
        dealId: deal.id,
      });

      setMessageText('');
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    };

    const handleFlowChange = (value: string) => {
      smsFlowStepMutation.mutate({
        flow: value as Flow,
      });
    };

    const handleFlowStepChange = (value: string) => {
      smsFlowStepMutation.mutate({
        flowStep: value as FlowStep,
      });
    };

    const handleKickoffConfirmFlow = () => {
      kickoffConfirmFlowMutation.mutate();
    };

    const hasMessages = !isEmpty(smsConversation);

    if (!isFeatureEnabled) {
      return null;
    }

    return (
      <Dialog modal={false}>
        <DialogTitle className="sr-only">Agent Conversation</DialogTitle>
        <DialogTrigger asChild>
          <DialogHeader>
            <Button ref={buttonRef} variant="outline" size="sm">
              <PDKMessageIcon />
              Agent Convo
            </Button>
          </DialogHeader>
        </DialogTrigger>

        <DialogContent
          className="fixed right-0 left-auto top-0 h-screen max-w-[96vw] sm:max-w-96 translate-x-0 translate-y-0 rounded-none border-l flex flex-col gap-4"
          onClickClose={() => {}}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogDescription className="sr-only">
            Agent Conversation displays the full SMS conversation history with
            the agent.
          </DialogDescription>
          <div className="text-sm  flex flex-row items-center justify-between gap-2 h-fit">
            <div className="flex flex-row items-center gap-2">
              <PDKMessageIcon className="h-4 w-4 text-blue-500" />
              Agent Conversation
            </div>
            <div className="flex flex-row items-center gap-2">
              <Button
                onClick={handleKickoffConfirmFlow}
                variant="outline"
                size="sm"
                className="h-7"
                disabled={kickoffConfirmFlowMutation.isPending}
              >
                {hasMessages ? 'Cont.' : 'Kickoff'}
              </Button>
              <Toggle
                pressed={isAgentPaused}
                onPressedChange={handleTogglePause}
                variant="outline"
                size="sm"
                className="h-7"
                disabled={agentConvoPauseMutation.isPending}
              >
                {isAgentPaused ? (
                  <>
                    <PDKResumeIcon />
                    Resume AI
                  </>
                ) : (
                  <>
                    <PDKPauseIcon />
                    Pause AI
                  </>
                )}
              </Toggle>
            </div>
          </div>

          {isAgentConvoReplyEnabled && (
            <div className="sticky top-0 text-xs text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-md p-2 bg-gray-50 dark:bg-gray-800">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <span>Flow:</span>
                  <Select
                    value={deal?.sms_flow || ''}
                    onValueChange={handleFlowChange}
                    disabled={smsFlowStepMutation.isPending}
                  >
                    <SelectTrigger className="h-7 flex-1">
                      <SelectValue placeholder="Select flow" />
                    </SelectTrigger>
                    <SelectContent>
                      {FLOW_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <span>Step:</span>
                  <Select
                    value={deal?.sms_flow_step || ''}
                    onValueChange={handleFlowStepChange}
                    disabled={smsFlowStepMutation.isPending}
                  >
                    <SelectTrigger className="h-7 flex-1">
                      <SelectValue placeholder="Select flow step" />
                    </SelectTrigger>
                    <SelectContent>
                      {FLOW_STEP_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          <div className="flex-1 overflow-y-auto space-y-3 px-2">
            {smsConversation &&
              smsConversation.map((message) => {
                const formattedTime = message.message_date
                  ? dayjs(message.message_date).local().format('MMM D, h:mm A')
                  : null;

                const isDrafting = message.message_status === 'drafting';

                return (
                  <div
                    key={message.id}
                    className={`flex ${message.direction === 'inbound' ? 'justify-start' : 'justify-end'}`}
                  >
                    <div
                      className={`max-w-[85%] ${message.direction === 'inbound' ? 'order-1' : 'order-2'}`}
                    >
                      <div
                        className={`rounded-2xl px-3 py-2 shadow-sm ${
                          isDrafting
                            ? 'bg-gray-100 dark:bg-gray-800 border-2 border-dashed border-gray-400 dark:border-gray-600'
                            : message.direction === 'inbound'
                              ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                              : 'bg-blue-500 text-white'
                        }`}
                      >
                        <div
                          className={`text-sm leading-relaxed break-words whitespace-pre-wrap ${
                            isDrafting
                              ? 'text-gray-600 dark:text-gray-400 italic'
                              : ''
                          }`}
                        >
                          {message.text}
                        </div>
                      </div>
                      <div
                        className={`text-xs text-gray-500 dark:text-gray-400 mt-1 px-2 ${
                          message.direction === 'inbound'
                            ? 'text-left'
                            : 'text-right'
                        }`}
                      >
                        {isDrafting ? (
                          <span className="text-blue-400">Drafting...</span>
                        ) : (
                          <>
                            {message.direction === 'inbound'
                              ? 'Customer'
                              : 'Agent'}{' '}
                            • {formattedTime}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>

          {isAgentConvoReplyEnabled && (
            <div className="flex gap-2 border-t pt-3 items-end">
              <Textarea
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="Type a message to the customer..."
                className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg"
                disabled={sendSMSMutation.isPending}
                startingRows={3}
              />
              <Button
                onClick={handleSendMessage}
                disabled={!messageText.trim() || sendSMSMutation.isPending}
                size="sm"
                className="px-3 self-end"
              >
                {sendSMSMutation.isPending ? (
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    );
  },
);

export default AgentConvo;
